import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Loader2, Upload, Film, Tv, DollarSign, BarChart3,
  ShoppingBag, X, Subtitles, ListVideo,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import * as tus from "tus-js-client";

const inp = "w-full bg-ink border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-brand";
const toArr = (s) => (Array.isArray(s) ? s : String(s || "").split(",").map((x) => x.trim()).filter(Boolean));
const toStr = (a) => (Array.isArray(a) ? a.join(", ") : a || "");
const Field = ({ label, children }) => (<div><label className="text-xs uppercase tracking-widest text-white/40">{label}</label><div className="mt-1">{children}</div></div>);

function emptyForm(type) {
  return {
    title: "", type, description: "", genres: "", runtime: 0, release_year: 2025, cast: "",
    director: "", rating: 0, maturity: "All", poster_url: "", backdrop_url: "", trailer_url: "",
    video_url: "", qualities: "1080p, 4K", audio_tracks: "Burmese, English",
    subtitles: "Burmese, English", subtitles_enabled: true,
    rent_price: 3, buy_price: 15, rent_days: 3, episodes: [],
  };
}

function UploadBtn({ label, onUploaded, accept }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(null);
  const isVideo = (accept || "").includes("video");

  const pollReady = async (uid) => {
    for (let i = 0; i < 120; i++) {
      try {
        const { data } = await api.get(`/distributor/stream/status/${uid}`);
        if (data.hls_url) return data.hls_url;
      } catch (e) { /* keep polling */ }
      await new Promise((r) => setTimeout(r, 4000));
    }
    return null;
  };

  const uploadToCloudflare = async (f) => {
    const { data } = await api.post("/distributor/stream/direct-upload");
    await new Promise((resolve, reject) => {
      const up = new tus.Upload(f, {
        endpoint: data.upload_url,
        chunkSize: 50 * 1024 * 1024,
        metadata: { filename: f.name, filetype: f.type },
        onError: reject,
        onProgress: (sent, total) => setPct(Math.round((sent / total) * 100)),
        onSuccess: resolve,
      });
      up.start();
    });
    setPct(null);
    toast.info(`${label}: processing video…`);
    const hls = await pollReady(data.video_uid);
    if (!hls) throw new Error("processing-timeout");
    return hls;
  };

  const handle = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      if (isVideo) {
        const url = await uploadToCloudflare(f);
        onUploaded(url); toast.success(`${label} ready to stream`);
      } else {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/distributor/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onUploaded(data.url); toast.success(`${label} uploaded`);
      }
    } catch (err) {
      console.error(`${label} upload failed:`, err);
      toast.error(err?.message === "processing-timeout" ? `${label}: still processing — paste the URL later` : `${label} upload failed`);
    } finally { setBusy(false); setPct(null); }
  };

  return (
    <>
      <input ref={ref} type="file" accept={accept} onChange={handle} className="hidden" />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="shrink-0 bg-white/10 hover:bg-white/20 rounded-md px-3 flex items-center gap-1 text-sm transition-colors" title={isVideo ? "Upload video to Cloudflare Stream" : "Upload"}>
        {busy ? (pct !== null ? <span className="text-xs tabular-nums px-1">{pct}%</span> : <Loader2 className="w-4 h-4 animate-spin" />) : <Upload className="w-4 h-4" />}
      </button>
    </>
  );
}

function UrlField({ label, field, value, accept, onSet, testid }) {
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <input data-testid={testid} value={value || ""} onChange={(e) => onSet(field, e.target.value)} placeholder="Paste URL or upload" className={inp} />
        <UploadBtn label={label} accept={accept} onUploaded={(url) => onSet(field, url)} />
      </div>
    </Field>
  );
}

export default function DistributorDashboard() {
  const { user, checked } = useAuth();
  const [tab, setTab] = useState("movie");
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm("movie"));
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/distributor/content").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    if (user && ["distributor", "admin"].includes(user.role)) {
      load();
      api.get("/distributor/stats").then((r) => setStats(r.data)).catch(() => {});
      api.get("/distributor/sales").then((r) => setSales(r.data)).catch(() => {});
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps -- reload distributor catalog on auth change

  if (checked && (!user || !["distributor", "admin"].includes(user.role))) return <Navigate to="/distributors" replace />;
  if (!items) return <div className="min-h-screen flex items-center justify-center bg-ink"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>;

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  const openNew = () => { setForm(emptyForm(tab)); setEditingId(null); setOpen(true); };
  const openEdit = (it) => {
    setForm({ ...emptyForm(it.type), ...it, genres: toStr(it.genres), cast: toStr(it.cast),
      qualities: toStr(it.qualities), audio_tracks: toStr(it.audio_tracks), subtitles: toStr(it.subtitles),
      subtitles_enabled: it.subtitles_enabled !== false, episodes: it.episodes || [] });
    setEditingId(it.id); setOpen(true);
  };

  const addEpisode = () => set("episodes", [...form.episodes, {
    id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())), season: 1,
    episode: form.episodes.length + 1, title: "", runtime: 0, thumbnail: "", video_url: "",
    rent_price: form.rent_price, buy_price: form.buy_price }]);
  const setEp = (i, k, v) => set("episodes", form.episodes.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const delEp = (i) => set("episodes", form.episodes.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    const payload = {
      ...form, runtime: Number(form.runtime) || 0, release_year: Number(form.release_year) || 2025,
      rating: Number(form.rating) || 0, rent_price: Number(form.rent_price) || 0,
      buy_price: Number(form.buy_price) || 0, rent_days: Number(form.rent_days) || 3,
      genres: toArr(form.genres), cast: toArr(form.cast), qualities: toArr(form.qualities),
      audio_tracks: toArr(form.audio_tracks), subtitles: toArr(form.subtitles),
      episodes: form.type === "series" ? form.episodes.map((e) => ({ ...e,
        runtime: Number(e.runtime) || 0, episode: Number(e.episode) || 1, season: Number(e.season) || 1,
        rent_price: Number(e.rent_price) || 0, buy_price: Number(e.buy_price) || 0 })) : null,
    };
    try {
      if (editingId) await api.put(`/distributor/content/${editingId}`, payload);
      else await api.post("/distributor/content", payload);
      toast.success(editingId ? "Updated" : "Published");
      setOpen(false); await load();
      api.get("/distributor/stats").then((r) => setStats(r.data));
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try { await api.delete(`/distributor/content/${it.id}`); setItems((c) => c.filter((x) => x.id !== it.id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const list = items.filter((i) => i.type === tab);
  const StatCard = ({ icon, label, value }) => (
    <div className="bg-surface border border-white/10 rounded-xl p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-lg bg-gold/15 text-gold flex items-center justify-center">{icon}</div>
      <div><p className="font-display text-2xl font-black">{value}</p><p className="text-white/50 text-xs uppercase tracking-widest">{label}</p></div>
    </div>
  );

  return (
    <div className="bg-ink min-h-screen pt-24 px-4 sm:px-12 pb-20">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Distributor Dashboard</h1>
          <p className="text-white/50">{user.company || user.name} · manage your catalogue, pricing & sales.</p>
        </div>
        {tab !== "sales" && <Button data-testid="dist-add-btn" onClick={openNew} className="bg-gold text-black hover:bg-gold/90"><Plus className="w-4 h-4 mr-1" /> Upload {tab === "movie" ? "Movie" : "TV Show"}</Button>}
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={<DollarSign className="w-5 h-5" />} label="Your Earnings" value={`$${stats.earnings}`} />
          <StatCard icon={<BarChart3 className="w-5 h-5" />} label="Gross Sales" value={`$${stats.gross}`} />
          <StatCard icon={<ShoppingBag className="w-5 h-5" />} label="Units Sold" value={stats.units_sold} />
          <StatCard icon={<Film className="w-5 h-5" />} label="Titles" value={stats.titles} />
        </div>
      )}

      <div className="flex gap-1 mb-6 border-b border-white/10">
        {[["movie", "Movies", <Film className="w-4 h-4" />], ["series", "TV Shows", <Tv className="w-4 h-4" />], ["sales", "Sales", <DollarSign className="w-4 h-4" />]].map(([k, l, ic]) => (
          <button key={k} data-testid={`dtab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 font-semibold text-sm -mb-px border-b-2 flex items-center gap-2 transition-colors ${tab === k ? "border-gold text-white" : "border-transparent text-white/50 hover:text-white"}`}>{ic} {l}</button>
        ))}
      </div>

      {tab !== "sales" ? (
        <div className="bg-surface border border-white/10 rounded-xl overflow-hidden">
          {list.map((it) => (
            <div key={it.id} data-testid={`dist-row-${it.id}`} className="flex items-center gap-4 px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors">
              {it.poster_url ? <img src={it.poster_url} alt="" className="w-10 h-14 object-cover rounded shrink-0 bg-ink" /> : <div className="w-10 h-14 rounded shrink-0 bg-ink flex items-center justify-center text-white/20"><Film className="w-4 h-4" /></div>}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{it.title}</p>
                <p className="text-white/40 text-xs">{it.release_year} · Rent ${it.rent_price} · Buy ${it.buy_price}{it.type === "series" ? ` · ${it.episodes?.length || 0} eps` : ""}</p>
              </div>
              <button data-testid={`dist-edit-${it.id}`} onClick={() => openEdit(it)} className="w-8 h-8 rounded-md hover:bg-white/10 flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`dist-del-${it.id}`} onClick={() => remove(it)} className="w-8 h-8 rounded-md hover:bg-brand/20 text-brand flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {list.length === 0 && <p className="text-white/40 text-center py-10">No {tab === "movie" ? "movies" : "TV shows"} yet. Click Upload to add one.</p>}
        </div>
      ) : (
        <div className="bg-surface border border-white/10 rounded-xl overflow-x-auto">
          <div className="min-w-[700px]">
            <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-widest text-white/40 border-b border-white/5">
              <div className="col-span-4">Buyer (email)</div><div className="col-span-3">Title</div>
              <div className="col-span-2">Type</div><div className="col-span-1 text-right">Paid</div><div className="col-span-2 text-right">You earn</div>
            </div>
            {sales.map((s) => (
              <div key={s.session_id} data-testid={`sale-${s.session_id}`} className="grid grid-cols-12 px-4 py-3 border-b border-white/5 text-sm items-center">
                <div className="col-span-4 truncate">{s.email}</div>
                <div className="col-span-3 truncate">{s.content_title}</div>
                <div className="col-span-2"><span className={`text-xs uppercase font-semibold ${s.mode === "buy" ? "text-gold" : "text-brand"}`}>{s.mode}</span></div>
                <div className="col-span-1 text-right">${s.amount}</div>
                <div className="col-span-2 text-right text-gold font-semibold">${s.seller_amount} <span className="text-white/30 text-xs">({Math.round((s.commission_rate || 0) * 100)}% fee)</span></div>
              </div>
            ))}
            {sales.length === 0 && <p className="text-white/40 text-center py-10">No sales yet.</p>}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit" : "Upload"} {form.type === "movie" ? "Movie" : "TV Show"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Title"><input data-testid="dist-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inp} /></Field>
            <Field label="Description / Caption"><textarea data-testid="dist-form-desc" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={inp} /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Genres (comma)"><input value={form.genres} onChange={(e) => set("genres", e.target.value)} className={inp} /></Field>
              <Field label="Cast (comma)"><input value={form.cast} onChange={(e) => set("cast", e.target.value)} className={inp} /></Field>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field label="Year"><input type="number" value={form.release_year} onChange={(e) => set("release_year", e.target.value)} className={inp} /></Field>
              <Field label="Runtime"><input type="number" value={form.runtime} onChange={(e) => set("runtime", e.target.value)} className={inp} /></Field>
              <Field label="Rating"><input type="number" step="0.1" value={form.rating} onChange={(e) => set("rating", e.target.value)} className={inp} /></Field>
              <Field label="Maturity"><input value={form.maturity} onChange={(e) => set("maturity", e.target.value)} className={inp} /></Field>
            </div>
            <Field label="Director"><input value={form.director} onChange={(e) => set("director", e.target.value)} className={inp} /></Field>

            <UrlField label="Poster Image" field="poster_url" value={form.poster_url} accept="image/*" onSet={set} testid="dist-poster" />
            <UrlField label="Backdrop Image" field="backdrop_url" value={form.backdrop_url} accept="image/*" onSet={set} testid="dist-backdrop" />
            {form.type === "movie" && <UrlField label="Movie Video (1080p/4K mp4 or .m3u8)" field="video_url" value={form.video_url} accept="video/*" onSet={set} testid="dist-video" />}
            <UrlField label="Trailer" field="trailer_url" value={form.trailer_url} accept="video/*" onSet={set} testid="dist-trailer" />

            <div className="grid grid-cols-3 gap-4">
              <Field label="Quality"><input value={form.qualities} onChange={(e) => set("qualities", e.target.value)} className={inp} /></Field>
              <Field label="Audio Tracks"><input value={form.audio_tracks} onChange={(e) => set("audio_tracks", e.target.value)} className={inp} /></Field>
              <Field label="Subtitle Languages"><input data-testid="dist-subs" value={form.subtitles} onChange={(e) => set("subtitles", e.target.value)} className={inp} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.subtitles_enabled} onCheckedChange={(v) => set("subtitles_enabled", v)} /><Subtitles className="w-4 h-4 text-gold" /> Subtitles available (viewers can turn on/off)</label>

            <div className="grid grid-cols-3 gap-4 border-t border-white/10 pt-4">
              <Field label="Rent Price ($)"><input data-testid="dist-rent-price" type="number" step="0.5" value={form.rent_price} onChange={(e) => set("rent_price", e.target.value)} className={inp} /></Field>
              <Field label="Rent Days"><input type="number" value={form.rent_days} onChange={(e) => set("rent_days", e.target.value)} className={inp} /></Field>
              <Field label="Buy Price ($)"><input data-testid="dist-buy-price" type="number" step="0.5" value={form.buy_price} onChange={(e) => set("buy_price", e.target.value)} className={inp} /></Field>
            </div>

            {form.type === "series" && (
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-display font-bold text-gold flex items-center gap-2"><ListVideo className="w-4 h-4" /> Episodes ({form.episodes.length})</p>
                  <button type="button" data-testid="dist-add-episode" onClick={addEpisode} className="text-sm text-gold flex items-center gap-1 hover:text-gold/80"><Plus className="w-4 h-4" /> Add Episode</button>
                </div>
                <div className="space-y-3">
                  {form.episodes.map((ep, i) => (
                    <div key={ep.id} className="bg-ink rounded-lg p-3 space-y-2 border border-white/10">
                      <div className="flex items-center gap-2">
                        <input type="number" value={ep.season} onChange={(e) => setEp(i, "season", e.target.value)} className={`${inp} w-16`} title="Season" />
                        <input type="number" value={ep.episode} onChange={(e) => setEp(i, "episode", e.target.value)} className={`${inp} w-16`} title="Episode #" />
                        <input value={ep.title} onChange={(e) => setEp(i, "title", e.target.value)} placeholder="Episode title" className={`${inp} flex-1`} />
                        <button type="button" onClick={() => delEp(i)} className="text-brand p-2"><X className="w-4 h-4" /></button>
                      </div>
                      <div className="flex gap-2">
                        <input value={ep.video_url} onChange={(e) => setEp(i, "video_url", e.target.value)} placeholder="Episode video URL" className={`${inp} flex-1`} />
                        <UploadBtn label="Episode video" accept="video/*" onUploaded={(url) => setEp(i, "video_url", url)} />
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <input type="number" value={ep.runtime} onChange={(e) => setEp(i, "runtime", e.target.value)} placeholder="Min" className={inp} />
                        <input value={ep.thumbnail} onChange={(e) => setEp(i, "thumbnail", e.target.value)} placeholder="Thumb URL" className={`${inp} col-span-1`} />
                        <input type="number" step="0.5" value={ep.rent_price} onChange={(e) => setEp(i, "rent_price", e.target.value)} placeholder="Rent $" className={inp} />
                        <input type="number" step="0.5" value={ep.buy_price} onChange={(e) => setEp(i, "buy_price", e.target.value)} placeholder="Buy $" className={inp} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70">Cancel</Button>
            <Button data-testid="dist-form-save" onClick={save} disabled={saving} className="bg-gold text-black hover:bg-gold/90">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} {editingId ? "Save" : "Publish"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
